/**
 * STAIR STUDIO stage 6 — the SIGNATURE PRESETS (docs/bim-plan/30-stair-studio.md §"Signature
 * presets"; Matt's decision 1: "the look lives on each stair, started from a firm preset").
 *
 * A preset is a whole LOOK — structure, treads, risers, details, lighting and guards — applied to a
 * stair without touching its configuration (its runs and turns stay; bim-plan 33). A side against a
 * wall gets the preset's wall rail; an open side its guard. Three presets are at their best on a
 * particular shape (`suits`): the Studio says so and offers to switch to it.
 */
import { tinted } from './colors'

/** every look key a preset sets — null clears it back to the stair's standard */
export const STAIR_LOOK_KEYS = [
  'structure', 'structureSize', 'treadThicknessIn', 'riserMode', 'treadMaterial', 'riserMaterial', 'structureMaterial',
  'treadFinish', 'nosingFt', 'nosingProfile', 'riserReveal', 'riserBar', 'soffit', 'skirt', 'knifeEdge', 'landingNosing',
  'skirtMaterial', 'soffitMaterial', 'ledNosing', 'ledUnderTread', 'stepLights', 'stepLightSide',
] as const

type Look = Partial<Record<(typeof STAIR_LOOK_KEYS)[number], string | number | boolean | null>>
type Guard = Record<string, string | number | boolean | null>

export interface StairPreset {
  id: string
  label: string
  note: string
  /** the configuration it is drawn for, if any (curved / spiral / imperial) */
  suits?: string
  look: Look
  /** the guard on an open side, and the rail on a side against a wall */
  open: Guard
  wall: Guard
}

const OAK = tinted('default', '#b48a5c'), WHITE_OAK = tinted('default', '#c9a67a'), PALE_OAK = tinted('default', '#d9b98c')
const WALNUT = tinted('default', '#6b4a33'), PAINT = tinted('default', '#f1eee7'), BLACK = tinted('default', '#2b2a28')
const LIMESTONE = tinted('default', '#d8cdb6'), TERRACOTTA = tinted('default', '#b5643c'), TILE = tinted('default', '#e9dcc2')
const CONCRETE = tinted('default', '#b9b4ab'), STONE = tinted('default', '#cfc6b4'), PLASTER = tinted('default', '#efe9df')
const STAINLESS = 'metal:stainless:default', BLACKENED = 'metal:blackened:default'

const wallRail = (rail = 'round', railMaterial: string | null = null): Guard => ({ guardKind: 'wallRail', guardRail: rail, railMaterial })

export const STAIR_PRESETS: StairPreset[] = [
  {
    id: 'coastalFloating', label: 'Modern Coastal Floating', note: 'Thick white-oak treads cantilevered from the wall, glass on a shoe, a glow under each tread',
    look: { structure: 'hiddenWall', treadThicknessIn: 3, riserMode: 'open', treadMaterial: WHITE_OAK, ledUnderTread: true },
    open: { guardKind: 'guard', guardInfill: 'glassShoe', guardRail: 'round', guardNewel: 'none', railMaterial: STAINLESS },
    wall: wallRail('round', STAINLESS),
  },
  {
    id: 'monoGallery', label: 'Monostringer Gallery', note: 'One steel tube under oak treads, glass between posts, step lights',
    look: { structure: 'monoHss', structureSize: 'hss8x8', riserMode: 'open', treadThicknessIn: 2.5, treadMaterial: OAK, structureMaterial: BLACKENED, stepLights: 'every2' },
    open: { guardKind: 'guard', guardInfill: 'glassPosts', guardRail: 'round', guardNewel: 'post', railMaterial: OAK },
    wall: wallRail('round', OAK),
  },
  {
    id: 'ribbon', label: 'Folded Steel Ribbon', note: 'A folded blackened-steel plate, flat-bar pickets, LED under the nosings',
    look: { structure: 'ribbon', riserMode: 'closed', structureMaterial: BLACKENED, ledNosing: true },
    open: { guardKind: 'guard', guardInfill: 'pickets', guardRail: 'squareBar', guardNewel: 'post' },
    wall: wallRail('squareBar', BLACKENED),
  },
  {
    id: 'spanishRevival', label: 'Spanish Revival', note: 'Santa Barbara / Laguna: plaster, terracotta treads, tiled risers, forged iron',
    look: { structure: 'solid', structureMaterial: PLASTER, treadMaterial: TERRACOTTA, riserMaterial: TILE, nosingProfile: 'bullnose', knifeEdge: true, stepLights: 'every2' },
    open: { guardKind: 'guard', guardInfill: 'iron', guardRail: 'squareBar', guardNewel: 'none' },
    wall: wallRail('squareBar', BLACK),
  },
  {
    id: 'mediterraneanGrand', label: 'Mediterranean Grand', note: 'Limestone treads, turned balusters and box newels — best as the imperial stair', suits: 'imperial',
    look: { structure: 'solid', structureMaterial: LIMESTONE, treadMaterial: LIMESTONE, riserMaterial: LIMESTONE, nosingProfile: 'bullnose', soffit: 'closed' },
    open: { guardKind: 'guard', guardInfill: 'balusters', guardRail: 'round', guardNewel: 'turned', infillMaterial: LIMESTONE, newelMaterial: LIMESTONE, railMaterial: WALNUT },
    wall: wallRail('round', WALNUT),
  },
  {
    id: 'craftsman', label: 'Craftsman', note: 'Housed stringers, oak treads on painted risers, square balusters, box newels',
    look: { structure: 'housed', riserMode: 'closed', treadMaterial: OAK, riserMaterial: PAINT, nosingProfile: 'eased', skirt: 'both' },
    open: { guardKind: 'guard', guardInfill: 'balusters', guardRail: 'rectWood', guardNewel: 'box', railMaterial: OAK, newelMaterial: OAK },
    wall: wallRail('rectWood', OAK),
  },
  {
    id: 'farmhouse', label: 'Transitional / Modern Farmhouse', note: 'White risers, oak treads, black pickets, a flat wood rail',
    look: { structure: 'woodCarriage', riserMode: 'closed', treadMaterial: OAK, riserMaterial: PAINT, nosingProfile: 'eased', skirt: 'wall' },
    open: { guardKind: 'guard', guardInfill: 'pickets', guardRail: 'rectWood', guardNewel: 'box', railMaterial: OAK, newelMaterial: PAINT },
    wall: wallRail('rectWood', OAK),
  },
  {
    id: 'helicalShowpiece', label: 'Curved Helical Showpiece', note: 'A plastered helix with a knife edge, glass on a shoe, LED under the nosings', suits: 'helical',
    look: { structure: 'solid', structureMaterial: PLASTER, treadMaterial: WHITE_OAK, knifeEdge: true, soffit: 'closed', ledNosing: true },
    open: { guardKind: 'guard', guardInfill: 'glassShoe', guardRail: 'round', guardNewel: 'none', railMaterial: WHITE_OAK },
    wall: wallRail('round', WHITE_OAK),
  },
  {
    id: 'cableContemporary', label: 'Cable Contemporary', note: 'Steel plate stringers, open oak treads, horizontal cable, step lights',
    look: { structure: 'steelPlate', riserMode: 'open', treadMaterial: OAK, structureMaterial: BLACKENED, stepLights: 'every2' },
    open: { guardKind: 'guard', guardInfill: 'cableH', guardRail: 'round', guardNewel: 'post', railMaterial: STAINLESS },
    wall: wallRail('round', STAINLESS),
  },
  {
    id: 'concreteMinimal', label: 'Concrete Minimal', note: 'Board-formed concrete, square nosings, glass and a recessed lit rail',
    look: { structure: 'concrete', structureMaterial: CONCRETE, nosingProfile: 'square', ledNosing: true },
    open: { guardKind: 'guard', guardInfill: 'glassShoe', guardRail: 'round', guardNewel: 'none', railMaterial: STAINLESS },
    wall: { guardKind: 'recessed', guardRail: 'round', railMaterial: STAINLESS },
  },
  {
    id: 'suspendedRod', label: 'Suspended Rod', note: 'Oak treads hung on steel rods from above, vertical rods for the guard',
    look: { structure: 'suspended', riserMode: 'open', treadThicknessIn: 2, treadMaterial: OAK, ledUnderTread: true },
    open: { guardKind: 'guard', guardInfill: 'cableV', guardRail: 'round', guardNewel: 'none', railMaterial: OAK },
    wall: wallRail('round', OAK),
  },
  {
    id: 'japandi', label: 'Timber Block / Japandi', note: 'Solid pale-oak blocks, a slender wood rail, a soft glow under each block',
    look: { structure: 'timberBlock', treadMaterial: PALE_OAK, ledUnderTread: true },
    open: { guardKind: 'guard', guardInfill: 'cableV', guardRail: 'rectWood', guardNewel: 'post', railMaterial: PALE_OAK, newelMaterial: PALE_OAK },
    wall: wallRail('rectWood', PALE_OAK),
  },
  {
    id: 'exteriorTerrace', label: 'Exterior Terrace (coastal)', note: 'Stone on concrete, glass on standoffs, a step light at every step',
    look: { structure: 'concrete', structureMaterial: CONCRETE, treadMaterial: STONE, stepLights: 'every', stepLightSide: 'both' },
    open: { guardKind: 'guard', guardInfill: 'glassStandoff', guardRail: 'round', guardNewel: 'none', guardMount: 'fascia', guardHeightIn: 42, railMaterial: STAINLESS },
    wall: wallRail('round', STAINLESS),
  },
  {
    id: 'spiralLoft', label: 'Spiral Loft / Roof Deck', note: 'A steel spiral with oak treads and forged pickets', suits: 'spiral',
    look: { structure: 'solid', treadMaterial: OAK, structureMaterial: BLACKENED },
    open: { guardKind: 'guard', guardInfill: 'iron', guardRail: 'squareBar', guardNewel: 'none' },
    wall: wallRail('squareBar', BLACK),
  },
]

export function stairPresetOf(id: string): StairPreset | null {
  return STAIR_PRESETS.find((p) => p.id === id) ?? null
}
